import { createFileRoute, Link } from "@tanstack/react-router";
import { Layers } from "lucide-react";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { KpiCard, Panel, RiskBadge, StatusChip } from "@/lovable-demo/components/bits";
import { C1000Chart, CostForecastChart, Sparkline } from "@/lovable-demo/components/charts";
import {
  blendedForecast,
  blendedSeries,
  portfolio,
  topFive,
} from "@/lovable-demo/lib/warranty/aggregate";
import { fmtCompactCurrency, fmtCurrency, fmtNumber } from "@/lovable-demo/lib/warranty/calc";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";

export const Route = createFileRoute("/demo/portfolio")({
  head: () => ({
    meta: [
      { title: "All Programs Combined — Broadview Warranty Intelligence" },
      {
        name: "description",
        content:
          "Combined warranty position across all vehicle programs: claims per 1,000, exposure, remaining exposure, recovery and a side-by-side program comparison.",
      },
      { property: "og:title", content: "All Programs Combined — Broadview" },
      {
        property: "og:description",
        content:
          "Combined warranty position across all vehicle programs with side-by-side comparison of claims per 1,000, exposure and recovery.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: PortfolioView,
});

function PortfolioView() {
  const { data } = useWarranty();
  const all = data.records;
  const stats = portfolio(all, data.programs);
  const five = topFive(all);
  const c1000 = blendedSeries(all, "c1000");
  const forecast = blendedForecast(all);

  const rows = data.programs.map((p) => {
    const recs = all.filter((r) => r.programId === p.id);
    const s = portfolio(recs, [p]);
    return { program: p, recs, s };
  });

  const totalUnits = stats.units;
  const totalClaims = stats.claims;
  const combinedC1000 = stats.c1000;
  const weightedTarget =
    data.programs.reduce((a, p) => a + p.c1000Target * p.unitsInService, 0) /
    Math.max(totalUnits, 1);
  const overTarget = rows.filter(({ program: p, s }) => s.c1000 > p.c1000Target).length;

  return (
    <AppShell
      title="All Programs Combined"
      subtitle={`${data.programs.length} programs · ${fmtNumber(totalUnits)} units in service · consolidated position`}
    >
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Combined claims per 1,000"
          value={combinedC1000.toFixed(1)}
          delta={`weighted target ${weightedTarget.toFixed(1)}`}
          hint={`Scenario aggregate · ${fmtNumber(totalClaims)} claims / ${fmtNumber(totalUnits)} program units`}
          tone={combinedC1000 > weightedTarget ? "bad" : "good"}
        />
        <KpiCard
          label="Open problem records"
          value={String(stats.open)}
          delta={`${stats.critical} critical`}
          hint={`${stats.total} total · ${stats.closed} closed · ${stats.gated} at an evidence gate`}
          tone="warn"
        />
        <KpiCard
          label="Combined exposure"
          value={fmtCompactCurrency(stats.exposure)}
          delta={`${fmtCompactCurrency(stats.actual)} incurred`}
          hint="Actual + projected warranty cost, all programs"
          tone="bad"
        />
        <KpiCard
          label="Remaining exposure"
          value={fmtCompactCurrency(stats.npr)}
          delta={`${fmtCompactCurrency(stats.delay)}/mo delay cost`}
          hint={`${overTarget} of ${data.programs.length} programs above target`}
          tone="good"
        />
      </div>

      <div className="mt-4">
        <Panel
          title="Program comparison"
          subtitle="Every program side by side, with the combined total"
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-sm">
              <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">Program</th>
                  <th className="px-2 py-2 text-right font-medium">Units</th>
                  <th className="px-2 py-2 text-right font-medium">Claims</th>
                  <th className="px-2 py-2 text-right font-medium">C/1000</th>
                  <th className="px-2 py-2 text-right font-medium">Target</th>
                  <th className="px-2 py-2 text-left font-medium">Trend</th>
                  <th className="px-2 py-2 text-right font-medium">Records</th>
                  <th className="px-2 py-2 text-right font-medium">Exposure</th>
                  <th className="px-2 py-2 text-right font-medium">Incurred</th>
                  <th className="px-2 py-2 text-right font-medium">Remaining exposure</th>
                  <th className="px-2 py-2 text-right font-medium">Recovery open</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {rows.map(({ program: p, s }) => (
                  <tr key={p.id}>
                    <td className="px-2 py-2.5">
                      <span className="num block text-xs text-primary">{p.code}</span>
                      <span className="block max-w-[220px] truncate">{p.name}</span>
                      <span className="block text-xs text-muted-foreground">
                        MY{p.modelYear} · {p.region}
                      </span>
                    </td>
                    <td className="num px-2 py-2.5 text-right">{fmtNumber(p.unitsInService)}</td>
                    <td className="num px-2 py-2.5 text-right">{fmtNumber(s.claims)}</td>
                    <td
                      className={`num px-2 py-2.5 text-right font-semibold ${
                        s.c1000 > p.c1000Target ? "text-warning" : "text-success"
                      }`}
                    >
                      {s.c1000.toFixed(1)}
                    </td>
                    <td className="num px-2 py-2.5 text-right text-muted-foreground">
                      {p.c1000Target.toFixed(1)}
                    </td>
                    <td className="px-2 py-2.5">
                      <span className="block h-8 w-24">
                        <Sparkline data={p.trend} tone={s.c1000 > p.c1000Target ? "2" : "4"} />
                      </span>
                    </td>
                    <td className="num px-2 py-2.5 text-right">
                      {s.open}
                      <span className="text-muted-foreground">/{s.total}</span>
                    </td>
                    <td className="num px-2 py-2.5 text-right">{fmtCompactCurrency(s.exposure)}</td>
                    <td className="num px-2 py-2.5 text-right text-muted-foreground">
                      {fmtCompactCurrency(s.actual)}
                    </td>
                    <td className="num px-2 py-2.5 text-right text-success">
                      {fmtCompactCurrency(s.npr)}
                    </td>
                    <td className="num px-2 py-2.5 text-right text-warning">
                      {fmtCompactCurrency(Math.max(s.recoveryTarget - s.recovered, 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-border">
                <tr className="font-semibold">
                  <td className="px-2 py-2.5">
                    <span className="inline-flex items-center gap-1.5">
                      <Layers className="size-3.5 text-primary" /> All programs
                    </span>
                  </td>
                  <td className="num px-2 py-2.5 text-right">{fmtNumber(totalUnits)}</td>
                  <td className="num px-2 py-2.5 text-right">{fmtNumber(stats.claims)}</td>
                  <td className="num px-2 py-2.5 text-right">{combinedC1000.toFixed(1)}</td>
                  <td className="num px-2 py-2.5 text-right text-muted-foreground">
                    {weightedTarget.toFixed(1)}
                  </td>
                  <td className="px-2 py-2.5">
                    <span className="block h-8 w-24">
                      <Sparkline data={c1000} tone="1" />
                    </span>
                  </td>
                  <td className="num px-2 py-2.5 text-right">
                    {stats.open}
                    <span className="text-muted-foreground">/{stats.total}</span>
                  </td>
                  <td className="num px-2 py-2.5 text-right">
                    {fmtCompactCurrency(stats.exposure)}
                  </td>
                  <td className="num px-2 py-2.5 text-right text-muted-foreground">
                    {fmtCompactCurrency(stats.actual)}
                  </td>
                  <td className="num px-2 py-2.5 text-right text-success">
                    {fmtCompactCurrency(stats.npr)}
                  </td>
                  <td className="num px-2 py-2.5 text-right text-warning">
                    {fmtCompactCurrency(Math.max(stats.recoveryTarget - stats.recovered, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <Panel
          title="Illustrative combined C/1000 trend"
          subtitle="Monthly sample series; separate from the aggregate claim-total KPI"
          className="xl:col-span-2"
        >
          <C1000Chart data={c1000} target={Number(weightedTarget.toFixed(1))} />
        </Panel>
        <Panel title="Combined financial position" subtitle="All programs">
          <dl className="space-y-3 text-sm">
            {[
              ["Actual cost to date", fmtCurrency(stats.actual)],
              ["Total exposure", fmtCurrency(stats.exposure)],
              ["Remaining exposure", fmtCurrency(stats.npr)],
              ["Delay cost per month", fmtCurrency(stats.delay)],
              ["Supplier recovery target", fmtCurrency(stats.recoveryTarget)],
              ["Recovered to date", fmtCurrency(stats.recovered)],
            ].map(([k, v]) => (
              <div
                key={k}
                className="flex items-baseline justify-between gap-3 border-b border-border/60 pb-2"
              >
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="num font-semibold">{v}</dd>
              </div>
            ))}
          </dl>
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <Panel
          title="Combined cost vs projected cost"
          subtitle="Formula-based cumulative projection across all programs; not an AI model"
          className="xl:col-span-2"
        >
          <CostForecastChart data={forecast} />
        </Panel>
        <Panel title="Top 5 across all programs" subtitle="Risk-ranked portfolio wide">
          <ul className="divide-y divide-border">
            {five.map((r, i) => (
              <li key={r.id}>
                <Link
                  to="/demo/problem-record/$id"
                  params={{ id: r.id }}
                  className="flex items-center gap-3 py-3 transition-colors hover:text-primary"
                >
                  <span className="num w-5 text-sm font-semibold text-muted-foreground">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{r.title}</span>
                    <span className="num block text-xs text-muted-foreground">
                      {r.code} · {fmtCompactCurrency(r.financial.exposure)}
                    </span>
                    <span className="mt-1 flex flex-wrap gap-1.5">
                      <RiskBadge risk={r.risk} score={r.riskScore} />
                      <StatusChip status={r.status} />
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </AppShell>
  );
}
